import React, {
	useState,
	useCallback,
	useMemo,
	useEffect,
	memo,
	type ElementRef,
	type KeyboardEvent,
	type SyntheticEvent,
	type ChangeEvent,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import OpenIcon from '@atlaskit/icon/glyph/open';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { CellWrapper as CellWrapperNew } from '@atlassian/jira-business-fields/cell-wrapper';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/fixit-error-flag';
import { fg } from '@atlassian/jira-feature-gating';
import { useEditField } from '@atlassian/jira-issue-field-base';
import type { IssueKey } from '@atlassian/jira-shared-types';
import type { CellNavigationProps } from '../../../common/types/cell-navigation';
import { WrapperCommon } from '../../../common/ui/list-cell';
import messages from './messages';

export type TextCellProps = {
	issueKey: IssueKey;
	isEditable: boolean;
	fieldKey: string;
	fieldType: string;
	fieldValue: string | null;
	fieldName?: string;
	issueType?: string;
	issueTypeId?: string;
} & CellNavigationProps;

export const UrlCell = memo<TextCellProps>(
	({
		issueKey,
		fieldKey,
		isEditable,
		fieldValue,
		fieldType,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		cellRef,
		inputValue = fieldValue ?? '',
		onInputChange = noop,
	}: TextCellProps) => {
		const [textValueOld, setTextValueOld] = useState<string>(fieldValue || '');
		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);

		const textValue = fg('jira_list_keyboard_nav') ? inputValue : textValueOld;
		const setTextValue = fg('jira_list_keyboard_nav') ? onInputChange : setTextValueOld;

		const [{ value, error }, { saveValue, resetError }] = useEditField<string | null, null>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: fieldValue,
		});
		const { createAnalyticsEvent } = useAnalyticsEvents();

		useEffect(() => {
			// reset textValue when there's an error
			if (error && value !== textValue) {
				setTextValue(value ?? '');
			}
		}, [error, setTextValue, textValue, value]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in useEffect above
		 */
		const handleClick = useCallback(() => {
			if (isEditable && !editMode) {
				setEditMode(true);
				setTextValue(value || '');
			}
		}, [editMode, isEditable, setEditMode, setTextValue, value]);

		const saveAndClose = useCallback(
			(event: SyntheticEvent) => {
				if (!fg('jira_list_keyboard_nav')) {
					event.preventDefault();
				}

				if (editMode) {
					setEditMode(false);

					if (textValue !== value) {
						saveValue(textValue, null, createAnalyticsEvent({}));
					}
				}
			},
			[createAnalyticsEvent, editMode, saveValue, setEditMode, textValue, value],
		);

		const handleChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				setTextValue(event.target.value);
				resetError();
			},
			[resetError, setTextValue],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				!editMode && onKeyDown?.(e);
			},
			[editMode, onKeyDown],
		);

		const handleKeyDownNew = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && event.code === 'Escape') {
					setTextValue(value ?? '');
				}
				return false;
			},
			[editMode, setTextValue, value],
		);

		const cellOverrides = useMemo(
			() => () => ({
				onKeyDown: handleKeyDownNew,
			}),
			[handleKeyDownNew],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in handleKeyDown
		 */
		const handleTextFieldKeyDown = useCallback(
			(e: KeyboardEvent<HTMLInputElement>) => {
				if (e.key === 'Escape') {
					e.preventDefault();
					setTextValue(value || '');
					// settimeout used here as there is a conflict with textfield autofocus
					setTimeout(() => {
						wrapperRef?.current?.focus();
					});
				}
			},
			[setTextValue, value, wrapperRef],
		);

		const handleSubmit = useCallback(
			(event: SyntheticEvent) => {
				saveAndClose(event);
				// settimeout used here as there is a conflict with textfield autofocus
				if (!fg('jira_list_keyboard_nav')) {
					setTimeout(() => {
						wrapperRef?.current?.focus();
					});
				}
			},
			[saveAndClose, wrapperRef],
		);

		const cellContent = (
			<>
				{editMode ? (
					<form onSubmit={handleSubmit}>
						<Textfield
							autoFocus
							onBlur={saveAndClose}
							value={textValue}
							onChange={handleChange}
							onKeyDown={fg('jira_list_keyboard_nav') ? noop : handleTextFieldKeyDown}
						/>
					</form>
				) : (
					value != null && (
						<a href={value} target="_blank">
							<LinkText>{value}</LinkText>

							<LinkFollower data-component-selector="business-list.ui.list-view.url-cell.link-follower">
								<OpenIcon label={value} />
							</LinkFollower>
						</a>
					)
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<CellWrapperNew
					isEditable={isEditable}
					testId="business-list.ui.list-view.url-cell.url-cell-wrapper"
					hasPadding={false}
				>
					<Wrapper>{cellContent}</Wrapper>
				</CellWrapperNew>
			);
		}

		return (
			<WrapperOld
				isFocused={editMode || isSelected}
				onClick={handleClick}
				onFocus={onFocus}
				onBlur={onBlur}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				tabIndex={0}
				data-testid="business-list.ui.list-view.url-cell.url-cell-wrapper"
			>
				{cellContent}
			</WrapperOld>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkFollower = styled.span({
	width: token('space.300', '24px'),
	background: token('color.background.neutral.subtle', '#fff'),
	display: 'none',
	alignItems: 'center',
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkText = styled.span({
	whiteSpace: 'nowrap',
	maxWidth: '100%',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	flexShrink: 1,
});

/**
 * To be deleted when jira_list_keyboard_nav is cleaned up
 * @deprecated Now use cellWrapperStyles from @atlassian/jira-business-fields/cell-wrapper
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WrapperOld = styled(WrapperCommon)({
	cursor: 'text',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	form: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> input': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> div': {
			height: '100%',
		},
	},
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		maxWidth: '100%',
		margin: token('space.100', '8px'),
		position: 'relative',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'[data-component-selector="business-list.ui.list-view.url-cell.link-follower"]': {
				display: 'flex',
			},
		},
	},
});

WrapperOld.displayName = 'Wrapper';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flex: 1,
	cursor: 'text',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	form: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> input': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> div': {
			height: '100%',
		},
	},
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		maxWidth: '100%',
		margin: token('space.100', '8px'),
		position: 'relative',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'[data-component-selector="business-list.ui.list-view.url-cell.link-follower"]': {
				display: 'flex',
			},
		},
	},
});
